import React, { useEffect, useCallback } from 'react';
import {
  BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useDashboard } from '../../../contexts/DashboardContext';
import { ChartCard, ChartGrid, CustomTooltip, CHART_COLORS, formatCurrency, DonutChartWithLabels } from '../ChartComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortfolioOverviewTab = () => {
  const { dataCache, setTabData, loading, setTabLoading, errors, setTabError, filters, isTabCached } = useDashboard();
  
  const data = dataCache.portfolio;

  const fetchData = useCallback(async () => {
    if (isTabCached('portfolio')) return;
    
    setTabLoading('portfolio', true);
    setTabError('portfolio', null);
    
    try {
      const params = new URLSearchParams();
      if (filters.asOnDate) params.append('as_on_date', filters.asOnDate);
      if (filters.branch) params.append('branch', filters.branch);
      
      const response = await fetch(`${API_URL}/api/dashboard/portfolio-overview?${params}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const result = await response.json();
      setTabData('portfolio', result);
    } catch (err) {
      setTabError('portfolio', err.message);
    } finally {
      setTabLoading('portfolio', false);
    }
  }, [filters, isTabCached, setTabData, setTabLoading, setTabError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading.portfolio) {
    return (
      <ChartGrid>
        {[1, 2, 3, 4].map(i => <ChartCard key={i} title="" loading={true} />)}
      </ChartGrid>
    );
  }

  if (errors.portfolio) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Error loading data: {errors.portfolio}</p>
        <button onClick={fetchData} className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { charts, summary } = data;

  return (
    <ChartGrid>
      {/* Chart 1: Loan Portfolio by Product Type (Donut) */}
      <ChartCard title="Loan Portfolio by Product Type" subtitle="Outstanding balance distribution">
        <div className="h-64">
          <DonutChartWithLabels 
            data={charts.loan_by_product} 
            colors={CHART_COLORS.palette}
          />
        </div>
      </ChartCard>

      {/* Chart 2: Secured vs Unsecured Split (Donut) */}
      <ChartCard title="Secured vs Unsecured Split" subtitle="Portfolio security composition">
        <div className="h-64">
          <DonutChartWithLabels 
            data={charts.secured_unsecured} 
            colors={[CHART_COLORS.primary, CHART_COLORS.warning]}
          />
        </div>
      </ChartCard>

      {/* Chart 3: Loan vs Deposit Comparison (Horizontal Bar) */}
      <ChartCard title="Loan vs Deposit Comparison" subtitle="Total book size">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.loan_vs_deposit} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(0)}Cr`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#94a3b8" width={100} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                <Cell fill={CHART_COLORS.primary} />
                <Cell fill={CHART_COLORS.secondary} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Chart 4: Sector-wise Loan Distribution (Bar) */}
      <ChartCard title="Sector-wise Loan Distribution" subtitle="Outstanding by sector">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.loan_by_sector}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(0)}Cr`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={CHART_COLORS.tertiary} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </ChartGrid>
  );
};

export default PortfolioOverviewTab;
